import React, {ReactNode} from 'react';

import {Plural, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {CalendarEventEditorModelGuestChanges} from './calendarEventEditorDataModel';

const emailGuestsText = <Trans id="cal-event-editor-footer-email-guests">Email guests</Trans>;
const emailAffectedGuestsText = (
  <Trans id="cal-event-editor-footer-email-affected-guests">Email affected guests</Trans>
);

interface CalendarEventEditorFooterData {
  checkboxText: ReactNode;
  tooltip?: ReactNode;
}

interface ComputeCalendarEventEditorFooterDataParams {
  guestChanges: CalendarEventEditorModelGuestChanges;
  didGlobalFieldsChangeOmittingGuestChanges: boolean;
  isUnsavedDraft: boolean;
}

// For computing the footer data that depends on what changes have been made to CalendarEventEditorDataModel.
export function computeCalendarEventEditorFooterData({
  guestChanges,
  didGlobalFieldsChangeOmittingGuestChanges,
  isUnsavedDraft,
}: ComputeCalendarEventEditorFooterDataParams): CalendarEventEditorFooterData {
  const {existingGuestCount, removedGuestCount, newGuestCount, finalGuestCount} = guestChanges;

  // The footer data when newly creating an event.
  if (isUnsavedDraft) {
    return {
      checkboxText: (
        <Plural
          id="cal-event-editor-footer-create-new-event-email-guests"
          value={finalGuestCount}
          one="Email guest"
          other="Email guests"
        />
      ),
    };
  }

  // The footer data when editing an existing event.
  const didRemoveGuests = removedGuestCount > 0;
  const didAddGuests = newGuestCount > 0;
  const shouldSendUpdatesToExistingGuests =
    didGlobalFieldsChangeOmittingGuestChanges && existingGuestCount > 0;

  const invitationsToNewGuests = (
    <Plural
      id="cal-event-editor-footer-invitations"
      value={newGuestCount}
      one="Invitation to new guest"
      other="Invitations to new guests"
    />
  );

  const updatesToExistingGuests = (
    <Plural
      id="cal-event-editor-footer-updates"
      value={existingGuestCount}
      one="Update to current guest"
      other="Updates to current guests"
    />
  );

  const cancellationsToRemovedGuests = (
    <Plural
      id="cal-event-editor-footer-cancellations"
      value={removedGuestCount}
      one="Cancellation to removed guest"
      other="Cancellations to removed guests"
    />
  );

  if (didRemoveGuests && didAddGuests && shouldSendUpdatesToExistingGuests) {
    return {
      checkboxText: emailGuestsText,
      tooltip: (
        <ul>
          <li>{invitationsToNewGuests}</li>
          <li>{updatesToExistingGuests}</li>
          <li>{cancellationsToRemovedGuests}</li>
        </ul>
      ),
    };
  }

  if (didRemoveGuests && didAddGuests) {
    return {
      checkboxText: emailAffectedGuestsText,
      tooltip: (
        <ul>
          <li>{invitationsToNewGuests}</li>
          <li>{cancellationsToRemovedGuests}</li>
        </ul>
      ),
    };
  }

  if (didAddGuests && shouldSendUpdatesToExistingGuests) {
    return {
      checkboxText: emailGuestsText,
      tooltip: (
        <ul>
          <li>{invitationsToNewGuests}</li>
          <li>{updatesToExistingGuests}</li>
        </ul>
      ),
    };
  }

  if (didRemoveGuests && shouldSendUpdatesToExistingGuests) {
    return {
      checkboxText: emailGuestsText,
      tooltip: (
        <ul>
          <li>{updatesToExistingGuests}</li>
          <li>{cancellationsToRemovedGuests}</li>
        </ul>
      ),
    };
  }

  if (didAddGuests) {
    return {
      checkboxText: (
        <Plural
          id="cal-event-editor-footer-email-new-guests"
          value={newGuestCount}
          one="Email new guest"
          other="Email new guests"
        />
      ),
    };
  }

  if (didRemoveGuests) {
    return {
      checkboxText: (
        <Plural
          id="cal-event-editor-footer-email-removed-guests"
          value={removedGuestCount}
          one="Email removed guest"
          other="Email removed guests"
        />
      ),
    };
  }
  // Even if there's no one to send updates to, the text we display is "Email guests".
  // However, if there's exactly one guest to send updates to, we display "Email guest".
  return {
    checkboxText: (
      <Plural
        id="cal-event-editor-footer-email-all-guests"
        value={existingGuestCount}
        one="Email guest"
        other="Email guests"
      />
    ),
  };
}
